/**
 * Native IndexedDB Service
 * Provides persistent storage for images and blog posts without external dependencies.
 */

const DB_NAME = 'bordens_shopper_db';
const DB_VERSION = 1;
const STORES = {
  IMAGES: 'site_images',
  NEWS: 'site_news'
};

export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORES.IMAGES)) {
        db.createObjectStore(STORES.IMAGES);
      }
      if (!db.objectStoreNames.contains(STORES.NEWS)) {
        db.createObjectStore(STORES.NEWS, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const dbService = {
  // Image Methods
  async saveImage(key: string, base64: string): Promise<void> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.IMAGES, 'readwrite');
      const store = transaction.objectStore(STORES.IMAGES);
      const request = store.put(base64, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async getImage(key: string): Promise<string | null> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.IMAGES, 'readonly');
      const store = transaction.objectStore(STORES.IMAGES);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  },

  async getAllImages(): Promise<Record<string, string>> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.IMAGES, 'readonly');
      const store = transaction.objectStore(STORES.IMAGES);
      const request = store.getAll();
      const keysRequest = store.getAllKeys();
      
      request.onsuccess = () => {
        const results: Record<string, string> = {};
        keysRequest.onsuccess = () => {
          keysRequest.result.forEach((key, i) => {
            results[key as string] = request.result[i];
          });
          resolve(results);
        };
      };
      request.onerror = () => reject(request.error);
    });
  },

  async deleteImage(key: string): Promise<void> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.IMAGES, 'readwrite');
      const store = transaction.objectStore(STORES.IMAGES);
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  // News/Blog Methods
  async getAllPosts(): Promise<any[]> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.NEWS, 'readonly');
      const store = transaction.objectStore(STORES.NEWS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  async savePost(post: any): Promise<void> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.NEWS, 'readwrite');
      const store = transaction.objectStore(STORES.NEWS);
      const request = store.put(post);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async deletePost(id: string): Promise<void> {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORES.NEWS, 'readwrite');
      const store = transaction.objectStore(STORES.NEWS);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
};